import moment from "moment";

import { H2RInput } from "../../imports";
import { H2RCheckbox } from "../../imports";
import { H2RSelect } from "../../imports";
import { ReplaceWithDataSource } from "../helpers/variable";
import { msToString, stringToMS, timeToDisplayString } from "../helpers/time";
import OpenExternal from "../helpers/OpenExternal";
import { H2RButton } from "../../imports";
import { faPause, faPlay } from "@fortawesome/free-solid-svg-icons";

export const GraphicEditor = ({ cue, media, onUpdate }) => {
	return (
		<>
			<H2RSelect
				selected={cue.timerType}
				label="Type"
				options={[
					{ label: "Current Date/Time", value: "time_of_day" },
					{ label: "To Time Of Day", value: "to_time_of_day" },
					{ label: "Countdown", value: "countdown" },
					{ label: "Count up", value: "countup" },
				]}
				onChange={(value) => {
					if (value === "to_time_of_day") {
						const m = moment().add("10", "minutes").format("hh:mm:ss");
						const end = moment().add("10", "minutes").format("x");
						onUpdate({ endTime: `${m}`, endTimestamp: end });
					} else if (value === "countdown") {
						onUpdate({
							timeLeft: 600000,
							duration: "00:10:00",
							durationMS: 600000,
						});
					} else if (value === "countup") {
						onUpdate({
							timeLeft: 0,
							startTime: "00:00:00",
							startTimeMS: 0,
							duration: "00:10:00",
							durationMS: 600000,
						});
					}
					onUpdate({ timerType: value });
				}}
			/>
			{cue.type === "big_time" && (
				<H2RSelect
					selected={cue.shape || "circle"}
					label="Shape"
					options={[
						{ label: "Circle", value: "circle" },
						{ label: "Line", value: "line" },
						{ label: "Mask", value: "mask" },
					]}
					onChange={(value) => {
						onUpdate({ shape: value });
					}}
				/>
			)}
			<div
				className={`${
					cue.timerType === "to_time_of_day" ? "visible" : "hidden"
				}`}
			>
				<H2RInput
					label="End time"
					type="time"
					key={cue.timerType}
					value={cue.endTime}
					valid={cue.endTimestamp > moment().format("x")}
					onChange={(value) => {
						const start = moment().format("X");
						const end = moment(value, "HH:mm:ss").format("X");
						const endTimestamp = moment(value, "HH:mm:ss").format("x");

						onUpdate({
							endTime: value,
							endTimestamp: endTimestamp,
							timeLeft: end - start,
						});
					}}
				/>
			</div>
			<div className={`${cue.timerType === "countup" ? "visible" : "hidden"}`}>
				<H2RInput
					label="Start time (hh:mm:ss)"
					key={cue.timerType}
					type="input"
					placeholder="00:00:00"
					value={cue.startTime}
					onChange={(value) => {
						onUpdate({ startTime: value, startTimeMS: stringToMS(value) });
					}}
				/>
			</div>
			<div
				className={`${
					cue.timerType === "countdown" || cue.timerType === "countup"
						? "visible"
						: "hidden"
				}`}
			>
				<>
					<H2RInput
						label="Duration (hh:mm:ss)"
						key={cue.timerType}
						type="input"
						placeholder="00:01:02"
						value={cue.duration}
						valid={cue.durationMS !== undefined}
						onChange={(value) => {
							onUpdate({
								timeLeft: cue.timerType === "countdown" ? stringToMS(value) : 0,
								duration: value,
								durationMS: stringToMS(value),
							});
						}}
					/>
				</>
			</div>
			{cue.timerType === "countdown" && (
				<H2RCheckbox
					checked={cue.hideOnMinus || true}
					label="Hide timer when time is up?"
					checkboxLabel={
						cue.hideOnMinus === false
							? "No, show negative numbers"
							: "Yes, hide when time is up"
					}
					onChange={(change) => {
						onUpdate({ hideOnMinus: change });
					}}
				/>
			)}
			<H2RInput
				label="Display format (Large text)"
				value={cue.format}
				placeholder="HH:mm:ss"
				onChange={(value) => {
					onUpdate({ format: value });
				}}
			/>
			<OpenExternal
				buttonText={"More formatting options"}
				isElectronLink={`https://docs.h2r.graphics/graphics/time#display-format`}
				isNotElectronLink={`https://docs.h2r.graphics/graphics/time#display-format`}
			/>
			{cue.timerType === "time_of_day" && (
				<H2RInput
					label="Timezone offset (+/- hours)"
					value={cue.timezoneOffset}
					placeholder="0"
					type="number"
					step={1}
					min={-12}
					max={12}
					onChange={(value) => {
						onUpdate({ timezoneOffset: value });
					}}
				/>
			)}
			{cue.type === "time" && (
				<H2RInput
					label="Pre message (Small text)"
					value={cue.pre}
					onChange={(value) => {
						onUpdate({ pre: value });
					}}
				/>
			)}
			<div
				className={`flex items-center space-x-2
								${
									cue.timerType === "countdown" || cue.timerType === "countup"
										? "visible"
										: "hidden"
								}
					`}
			>
				<H2RButton
					text={cue.paused !== true ? "Pause" : "Resume"}
					icon={cue.paused !== true ? faPause : faPlay}
					disabled={cue.status !== "onair"}
					colour="dark"
					size="small"
					onClick={() => {
						if (cue.paused) {
							onUpdate({
								paused: false,
								timestamp:
									moment().format("x") - (cue.pausedAtMS - cue.timestamp),
							});
						} else {
							onUpdate({ paused: true, pausedAtMS: moment().format("x") });
						}
					}}
				/>
			</div>
		</>
	);
};

export const GraphicCueContent = ({
	cue,
	dynamicText,
	dynamicLists,
	twoLines,
}) => {
	return (
		<div
			className={`overflow-ellipsis max-w-xs md:max-w-sm lg:max-w-lg overflow-hidden
						${twoLines ? "whitespace-normal" : "whitespace-nowrap"}
		`}
		>
			<span className="">
				{cue.timerType === "to_time_of_day" && (
					<>
						<span className="font-semibold">To time of day</span>
						<span className="text-gray-100 opacity-40 px-2">•</span>
						<span className="font-mono text-sm">{cue.endTime}</span>
					</>
				)}
				{cue.timerType === "time_of_day" && `Current time of day`}
				{cue.timerType === "countdown" && (
					<>
						<span className="font-semibold">Countdown</span>
						<span className="text-gray-100 opacity-40 px-2">•</span>
						<span className="font-mono text-sm">
							{cue.status === "onair"
								? msToString(cue.timeLeft)
								: timeToDisplayString(cue.duration)}
						</span>
					</>
				)}
				{cue.timerType === "countup" && (
					<>
						<span className="font-semibold">Count up</span>
						<span className="text-gray-100 opacity-40 px-2">•</span>
						<span className="font-mono text-sm">
							{cue.status === "onair" ? (
								<>
									{msToString(cue.timeLeft)}
									<span className="text-gray-100 opacity-40 px-2">/</span>
									{timeToDisplayString(cue.duration)}
								</>
							) : (
								timeToDisplayString(cue.duration)
							)}
						</span>
					</>
				)}
			</span>
			<span className="pl-2 text-xs text-gray-300">
				<ReplaceWithDataSource text={cue.pre} keepBrakets={true} styled={true} />
			</span>
		</div>
	);
};
